import React from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {AppText} from '@/shared/ui/text';
import {controls, radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * A segmented control: a small, closed set of alternatives where exactly one is on.
 *
 * Это `Segmented` ui-kit: ряд клавиш под общей рамкой на `surface-raised`, соседние
 * делятся одной линией, выбранная «утоплена» в цвет акцента с контрастной подписью.
 * Ряд из трёх отдельно обведённых ячеек читался бы как три кнопки; клавиши под
 * одной рамкой — как один переключатель, у которого одно положение всегда включено.
 *
 * Labels arrive as props; nothing user-facing is written here.
 */
export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
};

const styles = StyleSheet.create({
  chips: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  chip: {
    justifyContent: 'center',
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(1),
    borderRadius: radii.sm,
    borderWidth: 1,
    minHeight: controls.sm,
  },
  track: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    borderRadius: radii.sm,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  seg: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing(3),
    minHeight: controls.sm - 2,
  },
  segRule: {borderLeftWidth: StyleSheet.hairlineWidth},
  /**
   * Палец, а не указатель.
   *
   * Тема обещает, что контрол меньше 44pt носит `hitSlop`, добирающий касание до
   * пола. В вебе этого обещания нет: react-native-web не реализует `hitSlop` нигде,
   * кроме устаревшего `Touchable`, — а кабинет на телефоне это и есть веб-сборка,
   * поставленная как приложение. Поэтому НАРИСОВАННАЯ коробка и есть цель, и на
   * телефоне она растёт до 44. Так же поступает пилюля фильтра (`filter-bar`) и
   * так же устроен `TabRail`.
   */
  touch: {minHeight: controls.lg},
});

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  style,
  testID,
}: {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const {isDesktop} = useBreakpoint();

  return (
    <View
      testID={testID}
      accessibilityRole="tablist"
      style={[
        styles.track,
        {backgroundColor: p.card, borderColor: p.border},
        disabled && {opacity: 0.5},
        style,
      ]}>
      {options.map((option, i) => {
        const active = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="tab"
            accessibilityState={{selected: active, disabled}}
            accessibilityLabel={option.label}
            disabled={disabled || active}
            onPress={() => onChange(option.value)}
            style={state => [
              styles.seg,
              !isDesktop && styles.touch,
              i > 0 && [styles.segRule, {borderLeftColor: p.border}],
              {
                backgroundColor: active
                  ? p.primary
                  : isHot(state)
                  ? p.accent
                  : 'transparent',
              },
            ]}>
            <AppText
              variant="label"
              color={active ? p.primaryForeground : p.textSecondary}
              numberOfLines={1}>
              {option.label}
            </AppText>
          </Pressable>
        );
      })}
    </View>
  );
}

/** Курсор над клавишей или палец на ней. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * Choice chips: the same "pick one" as Segmented, but for a set too long or too
 * variable to share one track (department types, terminal profiles). They wrap, and
 * the chosen one is ui-kit's included toggle (`isActive` on a secondary button):
 * мягкая заливка акцентом и акцентная рамка — подсветка держится и без наведения.
 */
export function ChoiceChips<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  style,
  testID,
}: {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  return (
    <View testID={testID} style={[styles.chips, disabled && {opacity: 0.5}, style]}>
      {options.map(option => {
        const active = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="button"
            accessibilityState={{selected: active, disabled}}
            accessibilityLabel={option.label}
            disabled={disabled}
            onPress={() => onChange(option.value)}
            style={state => [
              styles.chip,
              !isDesktop && styles.touch,
              {
                backgroundColor: active
                  ? p.primarySoft
                  : isHot(state)
                  ? p.primarySofter
                  : 'transparent',
                borderColor: active || isHot(state) ? p.primary : p.borderStrong,
              },
            ]}>
            {state => (
              <AppText
                variant="body"
                color={active || isHot(state) ? p.primaryInk : p.foreground}>
                {option.label}
              </AppText>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

/**
 * A tab rail: one underlined row — ui-kit `Tabs`.
 *
 * `Segmented` above is a SETTING — a closed set of alternatives where exactly one is
 * on. A tab switches which VIEW of the same data you are looking at, and it belongs to
 * the content under it rather than to a form. Вкладки внутри раздела —
 * подчёркиванием: «таблетка» повторяла бы ленту разделов, и два разных
 * переключателя стали бы похожи. Активная — акцентная риска и акцентная подпись.
 */
export function TabRail<T extends string>({
  options,
  value,
  onChange,
  style,
  testID,
}: {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <View
      testID={testID}
      accessibilityRole="tablist"
      style={[tabStyles.rail, {borderBottomColor: p.border}, style]}>
      {options.map(option => {
        const active = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="tab"
            accessibilityState={{selected: active}}
            accessibilityLabel={option.label}
            onPress={() => onChange(option.value)}
            style={[tabStyles.tab, {borderBottomColor: active ? p.primary : 'transparent'}]}>
            {state => (
              <AppText
                variant="label"
                color={active ? p.primaryInk : isHot(state) ? p.foreground : p.mutedForeground}
                numberOfLines={1}>
                {option.label}
              </AppText>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

const tabStyles = StyleSheet.create({
  rail: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing(4),
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  tab: {
    minHeight: 36,
    justifyContent: 'center',
    paddingHorizontal: spacing(1),
    // -1 so the active rule sits ON the rail's hairline rather than above it.
    marginBottom: -StyleSheet.hairlineWidth,
    borderBottomWidth: 2,
  },
});
